import { modalIds } from '@const/modals';
import { apiWalletService } from '@api';
import { useLocales } from '@hooks/helpers/useLocales';
import useModalContext from '@hooks/context/useModalContext';
import { ECancelCardReason, ECardStatus, TCard, TCardTransaction } from '@xeppt/xeppt-sdk/types';
import { useUserContext } from '@hooks/context/useUserContext';
import { showToast } from '@components/toast';
import { routes } from '@const/routes';
import { useNavigate } from 'react-router-dom';

interface IProps {
    refetchCard?: () => void;
    card?: TCard;
}

export const useXepptCardModals = ({ refetchCard, card }: IProps) => {
    const { requestErrorLocale, requestSuccessLocale } = useLocales();
    const { onOpen, onClose, changeModalData, handleChangeLoading, modalData } = useModalContext();
    const { refetchAccount } = useUserContext();
    const navigate = useNavigate();

    const handleActivateCard = () => {
        onOpen({
            modalId: modalIds.VERIFY_CARD_ACTIVATION,
            onSubmit: (data: { first_6_digits: string }) => {
                if (data.first_6_digits === '421973') {
                    onOpen({
                        modalId: modalIds.USER_VERIFICATION_PHONE,
                        onSubmit: (securityData: {
                            secure_id: string;
                            code: string;
                            first_6_digits: string;
                        }) => {
                            handleChangeLoading(true);
                            apiWalletService
                                .activateCard(card?.id || '', data.first_6_digits, {
                                    id: securityData?.secure_id,
                                    code: securityData?.code
                                })
                                .then(() => {
                                    refetchCard && refetchCard();
                                    requestSuccessLocale('activate_card');
                                    onOpen({
                                        modalId: modalIds.SUCCESS_ACTIVATE_CARD,
                                        onSubmit: () => {
                                            handleOpenAddMoneyModal();
                                        }
                                    });
                                })
                                .catch(requestErrorLocale)
                                .finally(() => handleChangeLoading(false));
                        }
                    });
                } else {
                    showToast.error('Invalid card number');
                }
            }
        });
    };

    const handleOpeCardDetails = () => {
        // onOpen({
        //     modalId: modalIds.CARD_DETAILS_ENTER_PASSWORD,
        //     onSubmit: () => {
        //         changeModalData(card)
        //         onOpen({
        //             modalId: modalIds.CARD_DETAILS,
        //             onSubmit: onClose
        //         })
        //     }
        // });
        changeModalData(card);
        onOpen({
            modalId: modalIds.CARD_DETAILS,
            onSubmit: onClose
        });
    };

    const handleOpenLockCard = () => {
        onOpen({
            modalId: modalIds.USER_VERIFICATION_PHONE,
            onSubmit: () =>
                onOpen({
                    modalId: modalIds.LOCK_CARD,
                    onSubmit: () => onClose
                })
        });
    };

    const handleOpenUnlockCard = () => {
        onOpen({
            modalId: modalIds.USER_VERIFICATION_PHONE,
            onSubmit: () =>
                onOpen({
                    modalId: modalIds.UNLOCK_CARD,
                    onSubmit: () => onClose
                })
        });
    };

    const handleOpenAddMoneyModal = () => {
        changeModalData({ ...modalData, card });
        onOpen({
            modalId: modalIds.LOAD_CARD_BALANCE,
            onSubmit: (data: { amount: string }) => {
                handleChangeLoading(true);
                apiWalletService
                    .loadCardBalance(card?.id || '', Number(data?.amount))
                    .then(() => {
                        onClose();
                        requestSuccessLocale('load_card_balance');
                        refetchCard && refetchCard();
                        refetchAccount();
                    })
                    .catch(requestErrorLocale)
                    .finally(() => handleChangeLoading(false));
            }
        });
    };

    const handleOpenManageModal = (card?: TCard) => {
        changeModalData({ ...card, refetchCard });
        onOpen({
            modalId: modalIds.MANAGE_CREDIT_CARD
        });
    };

    const handleOpenCardAgreementModal = () => {
        changeModalData({ ...card });
        onOpen({
            modalId: modalIds.CARD_AGREEMENT_MODAL
        });
    };

    const handleOpenUpdatePinModal = () => {
        onOpen({
            modalId: modalIds.PIN_VERIFICATION_PHONE,
            onSubmit: () =>
                onOpen({
                    modalId: modalIds.UPDATE_PIN,
                    onSubmit: (data: {
                        id: string;
                        pin: string;
                        secure_id: string;
                        code: string;
                    }) => {
                        handleChangeLoading(true);
                        apiWalletService
                            .setCardPin(
                                {
                                    card_id: data?.id,
                                    pin: data?.pin
                                },
                                { id: data?.secure_id, code: data?.code }
                            )
                            .then(() => {
                                modalData?.refetchCard && modalData.refetchCard();
                                onOpen({
                                    modalId: modalIds.SUCCESS_UPDATE_PIN,
                                    onSubmit: () =>
                                        onOpen({
                                            modalId: modalIds.MANAGE_CREDIT_CARD,
                                            onSubmit: onClose
                                        })
                                });
                            })
                            .catch(requestErrorLocale)
                            .finally(() => handleChangeLoading(false));
                    }
                })
        });
    };

    const handleOpenLimitsModal = () => {
        onOpen({
            modalId: modalIds.CARD_LIMITS,
            onSubmit: () => {
                onClose();
            }
        });
    };

    const handleOpenReplaceCardModal = () => {
        onOpen({
            modalId: modalIds.REPLACE_CARD_REASON,
            onSubmit: () =>
                onOpen({
                    modalId: modalIds.USER_VERIFICATION_PHONE,
                    onSubmit: (data: {
                        secure_id: string;
                        code: string;
                        id: string;
                        card_holder: string;
                        reason_id: number;
                    }) => {
                        handleChangeLoading(true);
                        apiWalletService
                            .replaceCard(data?.id, {
                                id: data?.secure_id,
                                code: data?.code
                            })
                            .then(() => {
                                modalData?.refetchCard && modalData.refetchCard();
                                onOpen({
                                    modalId: modalIds.REPLACE_CARD_SUCCESS,
                                    onSubmit: () => {
                                        navigate(routes.xeppt_cards);
                                        onClose();
                                    }
                                });
                            })
                            .catch(requestErrorLocale)
                            .finally(() => handleChangeLoading(false));
                    }
                })
        });
    };

    const handleOpenCancelCardModal = () => {
        // onOpen({
        //     modalId: modalIds.CANCEL_CARD_PHONE_VERIFICATION,
        //     onSubmit: () =>
        onOpen({
            modalId: modalIds.CANCEL_CARD_REASON,
            onSubmit: () =>
                onOpen({
                    modalId: modalIds.USER_VERIFICATION_PHONE,
                    onSubmit: (data: {
                        secure_id: string;
                        code: string;
                        id: string;
                        reason_id: ECancelCardReason;
                        refetchCard?: () => void;
                    }) => {
                        handleChangeLoading(true);
                        apiWalletService
                            .cancelCard(data?.id, {
                                id: data?.secure_id,
                                code: data?.code
                            })
                            .then(() => {
                                data?.refetchCard && data?.refetchCard();
                                onClose();
                            })
                            .catch(requestErrorLocale)
                            .finally(() => handleChangeLoading(false));
                    }
                })
        });
        // });
    };

    const handleOpenViewCardTransaction = (transaction: TCardTransaction) => {
        // Pass the TCardTransaction as modalData directly (it carries .transaction +
        // merchant_name/transaction_type/transaction_id) — do NOT wrap, or the modal's
        // modalData.transaction would be the whole card object and amounts render NaN.
        changeModalData(transaction);
        onOpen({
            modalId: modalIds.VIEW_CARD_TRANSACTION
        });
    };

    const handleOpenFreezeCard = (id?: string) => {
        changeModalData({ id });
        onOpen({
            modalId: modalIds.FREEZE_CARD,
            onSubmit: () => {
                handleChangeLoading(true);
                setTimeout(() => {
                    apiWalletService
                        .freezeCard(id || '', ECardStatus.FREEZED)
                        .then(() => {
                            refetchCard && refetchCard();
                            requestSuccessLocale('freeze_card');
                            onClose();
                        })
                        .catch(requestErrorLocale)
                        .finally(() => handleChangeLoading(false));
                }, 500);
            }
        });
    };

    const handleOpenUnfreezeCard = (id?: string) => {
        changeModalData({ id });
        onOpen({
            modalId: modalIds.UNFREEZE_CARD,
            onSubmit: () => {
                handleChangeLoading(true);
                setTimeout(() => {
                    apiWalletService
                        .freezeCard(id || '', ECardStatus.ACTIVE)
                        .then(() => {
                            refetchCard && refetchCard();
                            requestSuccessLocale('unfreeze_card');
                            onClose();
                        })
                        .catch(requestErrorLocale)
                        .finally(() => handleChangeLoading(false));
                }, 500);
            }
        });
    };

    return {
        handleActivateCard,
        handleOpenUnlockCard,
        handleOpenLockCard,
        handleOpenCardAgreementModal,
        handleOpeCardDetails,
        handleOpenAddMoneyModal,
        handleOpenManageModal,
        handleOpenUpdatePinModal,
        handleOpenLimitsModal,
        handleOpenReplaceCardModal,
        handleOpenViewCardTransaction,
        handleOpenCancelCardModal,
        handleOpenFreezeCard,
        handleOpenUnfreezeCard
    };
};
